<template>
  <panel-layout
    id="history-panel"
    :loading="!!(history && history.loading) || false"
    :show-footer="false"
  >
    <template #heading>
      <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1 capitalize">
        {{ heading }}
      </h2>
    </template>

    <div class="h-full">
      <o-scroll class="p-5">
        <div v-if="historyAttributeOptions.length" class="mt-2 mb-2">
          <select-field v-model="attribute" :options="historyAttributeOptions" :placeholder="$t('pages.history.index.filterAttribute')" />
        </div>

        <o-table
          v-if="history && (history.loading || history.items.length)"
          v-model="selected"
          class="border rounded"
          :headers="headers"
          :data="history"
          :limit="20"
          :tbody-class="{
            tfoot: '',
            tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
            td: 'py-3 px-4 text-gray-800 text-sm break-words dark:text-gray-100 align-top',
            stripe: 'bg-white/80 dark:bg-white/[0.02]'
          }"
          :thead-class="{
            thead: 'border-b',
            tr: 'border-blue-700',
            th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
            content: 'flex item-center'
          }"
          striped
        >
          <template #row="props">
            <td :class="props.tdClass" class="w-14">
              <o-chip :variant="actionChipVariant(props.row.action)" size="xs" class="whitespace-nowrap flex-shrink-0">
                {{ $t(props.row.actionLabel) }}
              </o-chip>

              <o-chip v-if="props.row.relatedLabel" size="xs" class="whitespace-nowrap flex-shrink-0 mt-1">
                {{ props.row.relatedLabel }}
              </o-chip>
            </td>
            <td :class="props.tdClass" class="!break-normal whitespace-nowrap">
              <o-tooltip :text="relativeDate(props.row.created)">
                <span>{{ absoluteDate(props.row.created) }}</span>
              </o-tooltip>
            </td>
            <td :class="props.tdClass">
              <p v-if="props.row.eventDetail" v-html="props.row.eventDetail" />
              <ul class="space-y-2">
                <template v-for="(attributeHtml, attributeKey) in props.row.changedAttributeDetail" :key="attributeKey">
                  <li :class="{ filtered: attributeKey == attribute }">
                    <HistoryAttributeChange :html="attributeHtml" />
                  </li>
                </template>
              </ul>
            </td>
            <td :class="props.tdClass">
              <p v-html="props.row.authInfoLabel" />

              <div v-if="isAdmin || isDev" class="border-t dark:border-gray-800 mt-1 pt-1">
                <p v-if="props.row.adminAuthInfoLabel" v-html="props.row.adminAuthInfoLabel" />
                <p v-if="props.row.authSessionInfoLabel" v-html="props.row.authSessionInfoLabel" />
              </div>
            </td>
            <td v-if="isAdmin || isDev" :class="props.tdClass">
              <o-button
                flat
                outline
                size="xs"
                class="flex-shrink-0 w-14"
                :disabled="!canRevert(props.row)"
                @click.stop="handleRevert(props.row)"
              >
                {{ $t('general.revert') }}
              </o-button>
            </td>
          </template>
        </o-table>

        <div
          v-else-if="historyLoaded"
          class="h-full min-h-40 flex items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400"
          data-testid="history-empty-state"
        >
          {{ $t(historyLoadFailed ? 'pages.history.index.loadError' : 'pages.history.index.empty') }}
        </div>
      </o-scroll>
    </div>

    <o-modal
      ref="revertModal"
      :title="$t('pages.history.index.revertTitle')"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        { text: $t('general.revert'), type: 'confirm', variant: 'warning', callback: confirmRevert, disabled: !selectedRevertAttributes.length },
      ]"
      :loading="reverting"
      modal-class="relative m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-4xl sm:w-full h-full max-h-lg"
      content-class="mx-auto max-w-4xl h-full flex flex-col"
    >
      <template #body>
        <div v-if="revertItem" class="px-3 pt-5 pb-4 sm:px-4 flex-grow flex-shrink h-10 overflow-y-auto space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('pages.history.index.revertInfo') }}
          </p>

          <div class="border rounded overflow-hidden dark:border-gray-800">
            <div class="grid grid-cols-[1fr_auto] gap-4 px-4 py-3 font-semibold text-sm border-b bg-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <span>{{ $t('pages.history.index.changed') }}</span>
              <span>{{ $t('general.revert') }}</span>
            </div>
            <label
              v-for="revertAttribute in revertAttributes"
              :key="revertAttribute.key"
              class="grid grid-cols-[1fr_auto] gap-4 px-4 py-3 text-sm border-b last:border-b-0 items-start dark:border-gray-800"
            >
              <HistoryAttributeChange :html="revertAttribute.html" />
              <input
                v-model="selectedRevertAttributes"
                class="mt-1"
                type="checkbox"
                :value="revertAttribute.key"
                :aria-label="$t('pages.history.index.revertAttribute', { attribute: revertAttribute.key })"
              >
            </label>
          </div>
        </div>
      </template>
    </o-modal>
  </panel-layout>
</template>
<script setup lang="ts">
import { formatDistanceToNow } from 'date-fns'

import {
  TablePager,
} from '@/_api/lib/pager'

import {
  OPager,
  OTable,
} from '@/components/elements'

import {
  SelectField,
} from '@/components/fields'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { AuditActionType } from '~/_api/models/audit'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{
  modelType?: string
  primaryKey?: string
}>()

const nuxtApp = useNuxtApp()
const { $api, $notification, $config, $auth, $i18n } = nuxtApp
const $date = nuxtApp.$date as any

const attribute = ref<any>(null)
const selected = ref<any[]>([])
const historyLoaded = ref(false)
const historyLoadFailed = ref(false)
const revertModal = ref<{ open: () => void } | null>(null)
const revertItem = ref<any>(null)
const selectedRevertAttributes = ref<string[]>([])
const reverting = ref(false)

const { modelType, primaryKey } = props
const history = ref<any>(modelType && primaryKey ? new TablePager((page: any) => $api.audit.history(modelType, primaryKey, page, attribute.value), 20) : null)


const heading = computed(() => {
  const _modelType = props.modelType as string
  // Look up model label from $api services by converting PascalCase model name to camelCase service name
  const serviceKey = _modelType ? _modelType.charAt(0).toLowerCase() + _modelType.slice(1) : ''
  const service = ($api as any)[serviceKey]
  const modelLabel = service?.modelLabel || _modelType
  return $i18n.t('pages.history.index.heading', { model: modelLabel })
})

const headers = computed(() => {
  const _headers: any[] = [
    { text: $i18n.t('pages.history.index.action'), className: 'w-14' },
    { text: $i18n.t('general.dateTime') },
    { text: $i18n.t('pages.history.index.changed'), className: 'w-1/3' },
    { text: $i18n.t('general.user'), className: 'w-1/3' },
  ]

  if (isAdmin.value || isDev.value) {
    _headers.push($i18n.t('general.revert'))
  }

  return _headers
})

const isDev = computed(() => $config.public.instance === 'dev')
const isAdmin = computed(() => $auth?.info?.adminUser)
const revertAttributes = computed(() => changedAttributes(revertItem.value))
const historyAttributeOptions = computed(() => Object.entries(history.value?.list?.filter?.attributes ?? {})
  .map(([value, label]) => ({ value, label })))

const loadHistory = async (reload = false) => {
  if (!history.value) {
    historyLoaded.value = true
    return
  }

  historyLoadFailed.value = false

  try {
    if (reload) {
      await history.value.reload()
    } else {
      await history.value.load()
    }
  } catch (e: any) {
    historyLoadFailed.value = true
    $notification.error({ errors: e?.errors })
  } finally {
    historyLoaded.value = true
  }
}

watch(attribute, () => loadHistory(true))

onMounted(() => loadHistory())

const handleCancel = () => {
  panel.close()
}

const relativeDate = (created: number): string => {
  const date = $date.local(created * 1000)
  return formatDistanceToNow(date, { addSuffix: true })
}

const absoluteDate = (created: number): string => {
  return $date.format($date.local(created * 1000), 'EEEE, MMMM do yyyy h:mm:ss a') ?? ''
}

const changedAttributes = (row: any) => {
  return Object.entries(row?.changedAttributeDetail || {}).map(([key, html]) => ({ key, html: String(html) }))
}

const auditId = (row: any): string => {
  return row?.objectID?.() || row?.idAudit?.toString() || row?.id?.toString() || ''
}

const canRevert = (row: any): boolean => {
  return row?.canRevert !== false && !!auditId(row) && changedAttributes(row).length > 0
}

const handleRevert = (row: any) => {
  revertItem.value = row
  selectedRevertAttributes.value = changedAttributes(row).map(({ key }) => key)
  revertModal.value?.open()
}

const confirmRevert = async (): Promise<boolean> => {
  const id = auditId(revertItem.value)
  if (!id || !selectedRevertAttributes.value.length) {
    return false
  }

  reverting.value = true

  try {
    await $api.audit.revert(id, selectedRevertAttributes.value)
    $notification.toast({
      title: $i18n.t('pages.history.index.revertSuccess'),
      variant: 'success',
    })
    await history.value?.reload()

    return true
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  } finally {
    reverting.value = false
  }

  return false
}

const actionChipVariant = (action: any) => {
  switch (action) {
    case AuditActionType.Create:
      return 'success'
    case AuditActionType.Update:
      return 'info'
    default:
      return undefined
  }
}
</script>

<style scoped>
  .filtered {
    background-color: yellow;
  }
</style>
